// PaymentActionsWithKeypad.js
import React, { useEffect, useCallback, useRef } from 'react';
import { useAlert } from '../components/AlertModal';
import '../styles/PaymentActionsWithKeypad.css';

const PaymentActionsWithKeypad = ({
  totalAmount,
  salesList,
  sale_type,
  cashGiven, // 預り金額(現金)
  cardGiven, // 預り金額(キャッシュレス)
  change,
  taxRate,
  taxFreeMode,
  calculateTax,
  input,
  setSale_type,
  setCashGiven,
  setCardGiven,
  setChange,
  setChangeinfo,
  setPaymentMethod,
  setTax_exemption,
  handleCompletePayment,
  setInput,
  handleButtonfocusClick,
  isSalesDetailModalOpen,
}) => {
  const totalTax = calculateTax(totalAmount, taxRate);
  const totalWithTax = totalAmount + totalTax;

  const isTaxExempt = sale_type === 'tax_exempt';

  // ★従来方式の免税だけ税抜
  const isLegacyTaxExempt =
    isTaxExempt && taxFreeMode === "legacy";

  const Finalpaymentamount =
    isLegacyTaxExempt
      ? totalAmount
      : totalWithTax;
  const totalGiven = cashGiven + cardGiven ;
  const Remainingamount = Finalpaymentamount - totalGiven ;
  const { showAlert } = useAlert();

  const scanBufferRef = useRef("");
  const scanTimerRef = useRef(null);
  const unitModeRef = useRef(false);

  // 千・万で確定した部分
  const unitBaseAmountRef = useRef(0);

  // 千・万を押した後に入力した数字
  const unitAdditionalDigitsRef = useRef("");
  
  useEffect(() => {
    if (cashGiven > 0 && cardGiven === 0) {
      // 現金だけの場合
      setPaymentMethod("現金");
    }
    else if (cashGiven === 0 && cardGiven > 0) {
      // キャッシュレスだけの場合
      setPaymentMethod("キャッシュレス");
    }
    else if (cashGiven > 0 && cardGiven > 0) {
      // 現金＆キャッシュレスの場合
      setPaymentMethod("現金＆キャッシュレス");
    }
    else {
      // 現金もキャッシュレスもない場合
      setPaymentMethod("");
    }
  }, [cashGiven, cardGiven, setPaymentMethod]);


  useEffect(() => {
    if (totalGiven > 0) {
      const ProvisionaltotalChange = totalGiven - Finalpaymentamount;
      const ProvisionalcashChange = cashGiven - ProvisionaltotalChange;
      if (ProvisionalcashChange >= 0) {
        const result = ProvisionaltotalChange < 0 ? 0 : ProvisionaltotalChange;
        setChange(result); // おつりとして設定
      } else {
        const Changeinfo = "現金でおつりが返せません";  // おつりが返せない場合のメッセージ
        setChangeinfo(Changeinfo);
      }
    }    
  }, [totalGiven, Finalpaymentamount, cashGiven, setChange, setChangeinfo]); // totalGivenまたはFinalpaymentamountが変わるたびに実行

  useEffect(() => {
    if (change !== null) {
      const Changeinfo = "¥" + change.toLocaleString();  // おつりの金額を¥でフォーマットして格納
      setChangeinfo(Changeinfo);
    }
  }, [change, setChangeinfo]); // changeが更新されたときに実行
  
  // キーパッドのボタンが押されたときの処理
  const handleButtonClick = useCallback((value) => {
    if (unitModeRef.current) {
      // 千・万を押した後は、追加入力として扱う
      unitAdditionalDigitsRef.current += value;

      const additionalAmount =
        Number(unitAdditionalDigitsRef.current) || 0;

      setInput(
        String(unitBaseAmountRef.current + additionalAmount)
      );

      return;
    }

    // 通常入力
    setInput((prevInput) => prevInput + value);
  }, [setInput]);

  const resetUnitMode = useCallback(() => {
    unitModeRef.current = false;
    unitBaseAmountRef.current = 0;
    unitAdditionalDigitsRef.current = "";
  }, []);

  // 入力値をクリアする
  const handleClear = () => {
    resetUnitMode();
    setInput("");
  };
  const handleRemainingAmount = () => {
    const remaining =
      Finalpaymentamount - totalGiven > 0
        ? Finalpaymentamount - totalGiven
        : 0;

    resetUnitMode();
    setInput(String(remaining));
  };

  // 千・万ボタン
  const handleUnitButton = (unit) => {
    // まだ千・万モードではない場合
    if (!unitModeRef.current) {
      const normalized = String(input || "").replace(/,/g, "");
      const currentValue = Number(normalized) || 0;

      const result =
        currentValue > 0
          ? currentValue * unit
          : unit;

      unitModeRef.current = true;
      unitBaseAmountRef.current = result;
      unitAdditionalDigitsRef.current = "";

      setInput(String(result));
      return;
    }

    /*
    * 千・万モード中に、さらに千・万を押した場合
    */

    const additionalDigits = unitAdditionalDigitsRef.current;
    const additionalValue = Number(additionalDigits) || 0;

    if (additionalDigits) {
      // 例：千 → 2 → 千
      // 1000 + 2000 = 3000
      unitBaseAmountRef.current += additionalValue * unit;
    } else {
      // 例：千 → 千
      // 1000 + 1000 = 2000
      unitBaseAmountRef.current += unit;
    }

    unitAdditionalDigitsRef.current = "";

    setInput(String(unitBaseAmountRef.current));
  };

  const handleFieldChangeGiven = useCallback((field) => {
      // フィールドが「現金」なら
      if (field === 'cashgiven') {
        if ((sale_type === 'normal' && cardGiven === 0) || (sale_type === 'tax_exempt' && cardGiven === 0)) { 
          const inputValue = input && input.trim();  // 入力がnullでないか、または空でないかチェックしてから前後の空白を削除

          // 入力がnullや空でない場合に処理を行う
          if (inputValue) {
            const cashgivenValue = (cashGiven + parseFloat(inputValue)); // 入力値を数値に変換して既存のcashGivenと合計

            // 数値が有効かチェック
            if (!isNaN(cashgivenValue)) {
              setCashGiven(cashgivenValue);  // 現金預り金額設定
            }
          } else {
            // inputがnullまたは空文字の場合の処理（任意）
            if (cashGiven === 0) {
              setCashGiven(Finalpaymentamount);  // 現金預り金額設定
            }
          }
        } else {
          showAlert("カードとの併用は出来ません");
        }
      } else if (field === 'cardgiven') {
        if ((sale_type === 'normal' && cashGiven === 0) || (sale_type === 'tax_exempt' && cashGiven === 0)) { 
          const inputValue = input && input.trim();  // 入力がnullでないか、または空でないかチェックしてから前後の空白を削除

          // 入力がnullや空でない場合に処理を行う
          if (inputValue) {
            const cardGivenValue = (cardGiven + parseFloat(inputValue)); // 入力値を数値に変換して既存のcashGivenと合計

            // 数値が有効かチェック
            if (!isNaN(cardGivenValue)) {
              setCardGiven(cardGivenValue);  // 現金預り金額設定
            }
          } else {
            // inputがnullまたは空文字の場合の処理（任意）
            if (cardGiven === 0) {
              setCardGiven(Finalpaymentamount);  // 現金預り金額設定
            }
          }
        } else {
          showAlert("現金との併用は出来ません");
        }
      }
      resetUnitMode();
      setInput(''); // フィールド変更時に入力値をクリア 
    }, [
    input,
    cashGiven,
    cardGiven,
    sale_type,
    Finalpaymentamount,
    setCashGiven,
    setCardGiven,
    setInput,
    showAlert,
    resetUnitMode,
  ]);
  
  useEffect(() => {
    const handleKeyDown = (e) => {
      // モーダル開いてる時は無効
      if (isSalesDetailModalOpen) return;

      // Backspace
      if (e.code === "Backspace") {
        if (unitModeRef.current) {
          if (unitAdditionalDigitsRef.current.length > 0) {
            unitAdditionalDigitsRef.current =
              unitAdditionalDigitsRef.current.slice(0, -1);

            const additionalAmount =
              Number(unitAdditionalDigitsRef.current) || 0;

            setInput(
              String(unitBaseAmountRef.current + additionalAmount)
            );
          } else {
            // 追加入力がない場合は通常状態に戻す
            resetUnitMode();
            setInput((prev) => prev.slice(0, -1));
          }
        } else {
          setInput((prev) => prev.slice(0, -1));
        }

        e.preventDefault();
        return;
      }
      
      // Delete は全消し
      if (e.code === "Delete") {
        resetUnitMode();
        setInput("");
        e.preventDefault();
        return;
      }

      // テンキー数字
      if (!/^Numpad[0-9]$/.test(e.code)) return;

      const num = e.code.replace("Numpad", "");

      scanBufferRef.current += num;

      clearTimeout(scanTimerRef.current);

      scanTimerRef.current = setTimeout(() => {
        const value = scanBufferRef.current;
        scanBufferRef.current = "";

        // 10文字以上はバーコード扱い
        if (value.length >= 10) return;

        handleButtonClick(value);
      }, 80);

      e.preventDefault();
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [setInput, isSalesDetailModalOpen,handleButtonClick,resetUnitMode,]);

  // 免税小計
  const handleTaxFree = () => {
    setSale_type('tax_exempt');
    setTax_exemption(totalAmount);
    handleButtonfocusClick();
    setInput(''); // フィールド変更時に入力値をクリア 
  };

  // 通常小計
  const handleinTax = () => {
    setSale_type('normal');
    setTax_exemption(0);
    handleButtonfocusClick();
    setInput(''); // フィールド変更時に入力値をクリア 
  };


  return (
    <div className="paymentActionsWithKeypad">
      <div className="paymentActionsWithKeypad__buttons2">  
        <button className="action-button-subtotal" onClick={handleinTax}>小計</button>
        <button className="action-button-subtotal" onClick={handleTaxFree}>免税小計</button>
      </div>
        <div className="keypad__display">
          <input type="text" value={input} readOnly />
        </div>
      <div className="keypad">
        <div className="keypad__buttons">
          <button className="keypad-button" onClick={handleClear}>C</button>
          <button className="keypad-button-w2" onClick={handleRemainingAmount}>残金</button> 
          {[7, 8, 9, 4, 5, 6, 1, 2, 3].map((num) => (
            <button key={num} className="keypad-button" onClick={() => handleButtonClick(num.toString())}>{num}</button>
          ))}
          
          <button
            className="keypad-button-w2-l"
            onClick={() => handleButtonClick("0")}
          >
            0
          </button>

          <button
            className="keypad-button"
            onClick={() => handleButtonClick("00")}
          >
            00
          </button>
          <button
            className="keypad-button"
            onClick={() => handleUnitButton(1000)}
          >
            千
          </button>

          <button
            className="keypad-button"
            onClick={() => handleUnitButton(10000)}
          >
            万
          </button>
        </div>
      </div>
       <div className="paymentActionsWithKeypad__buttons3">  
        <button className="action-button-payment" onClick={() => handleFieldChangeGiven('cashgiven')}>現金</button>
        <button className="action-button-payment" onClick={() => handleFieldChangeGiven('cardgiven')}>CARD</button>
      </div>
      <div className="paymentActionsWithKeypad__buttons4">
        <button 
          className="keypad-button-w3" 
          onClick={handleCompletePayment} 
          disabled={ salesList.length < 1 || Remainingamount > 0}
        > 
          会計完了
        </button>
      </div>
    </div>
  );
};

export default PaymentActionsWithKeypad;
